import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";

import { api } from "../../convex/_generated/api";
import { getConvexHttpClient } from "./convexHttpClient";

export const getUserCountry = createServerFn({ method: "GET" }).handler(() => {
  const country =
    getRequestHeader("x-vercel-ip-country") ??
    getRequestHeader("cf-ipcountry") ??
    null;
  return typeof country === "string" && country.length > 0 ? country : null;
});

export type ServerLocalizedPrice = {
  amount: number;
  country: string | null;
  currency: string;
  formatted: string;
};

export const getServerLocalizedPrice = createServerFn({
  method: "GET",
}).handler(async (): Promise<ServerLocalizedPrice | null> => {
  const rawCountry =
    getRequestHeader("x-vercel-ip-country") ??
    getRequestHeader("cf-ipcountry") ??
    null;
  const country =
    typeof rawCountry === "string" && rawCountry.length > 0 ? rawCountry : null;

  try {
    const { amount, currency } = await getConvexHttpClient().action(
      api.stripeActions.getLocalizedPrice,
      { country: country ?? undefined },
    );
    return {
      amount,
      country,
      currency,
      formatted: formatLocalizedPrice(amount, currency),
    };
  } catch {
    // Stripe/Convex failure — Convex action's own debugAndThrow logs the cause.
    // Caller decides on the user-visible fallback.
    return null;
  }
});

const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

const LOCALE_BY_CURRENCY: Record<string, string> = {
  ars: "es-AR",
  aud: "en-AU",
  brl: "pt-BR",
  cad: "en-CA",
  chf: "de-CH",
  clp: "es-CL",
  cop: "es-CO",
  eur: "es-ES",
  gbp: "en-GB",
  mxn: "es-MX",
  nzd: "en-NZ",
  pen: "es-PE",
  usd: "en-US",
  uyu: "es-UY",
};

export function formatLocalizedPrice(amount: number, currency: string): string {
  const lower = currency.toLowerCase();
  const divisor = ZERO_DECIMAL_CURRENCIES.has(lower) ? 1 : 100;
  const locale = LOCALE_BY_CURRENCY[lower] ?? "es-ES";
  return new Intl.NumberFormat(locale, {
    currency: currency.toUpperCase(),
    style: "currency",
  }).format(amount / divisor);
}
